import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAppStore } from "../../store/useAppStore";
import type { Situation } from "../../domain/types";
import { assembledBrief, draftMessage } from "../../domain/jarvis";
import { StateTrack } from "../ui/StateTrack";
import { Chip } from "../ui/Chip";
import { Button } from "../ui/Button";
import { JarvisPanel } from "./JarvisPanel";
import { MyRisePreview } from "./MyRisePreview";

const priorityTone = { critical: "danger", high: "attention", medium: "operational", low: "operational" } as const;

export function SituationPanel({ situation }: { situation: Situation }) {
  const navigate = useNavigate();
  const store = useAppStore();
  const [draftUsed, setDraftUsed] = useState(false);
  const unit = situation.unitId ? store.units.find((u) => u.id === situation.unitId) : null;
  const tenant = unit?.tenantId ? store.tenants.find((t) => t.id === unit.tenantId) : null;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-action text-lg font-bold leading-6 text-foreground">{situation.title}</h2>
          <p className="mt-1 text-[13.5px] leading-5 text-muted-foreground">{situation.summary}</p>
        </div>
        <Chip tone={priorityTone[situation.priority]}>{situation.priority}</Chip>
      </div>

      {situation.workflow && (
        <div className="rounded-md border border-border bg-card p-3">
          <StateTrack steps={situation.workflow.steps} />
        </div>
      )}

      <JarvisPanel label={situation.id === "sit-hartley" ? "Jarvis · assembled situation" : "Jarvis"}>
        <p>{assembledBrief(situation)}</p>
      </JarvisPanel>

      <section aria-labelledby={`${situation.id}-sources-h`} className="rounded-md border border-border bg-card">
        <h3 id={`${situation.id}-sources-h`} className="border-b border-border px-3.5 py-2 font-action text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
          Verified facts
        </h3>
        <ul className="divide-y divide-border">
          {situation.sources.map((src) => (
            <li key={src.id} className="flex items-center justify-between gap-3 px-3.5 py-2.5">
              <span className="text-[13.5px] font-medium text-foreground">{src.label}</span>
              <span className="font-mono text-[10.5px] uppercase text-muted-foreground">{src.kind.replace("-", " ")}</span>
            </li>
          ))}
        </ul>
      </section>

      <CategoryActions situation={situation} draftUsed={draftUsed} setDraftUsed={setDraftUsed} />

      {tenant && (situation.category === "financial" || situation.category === "support" || situation.category === "move-in") && (
        <MyRisePreview unitId={unit!.id} />
      )}

      <div className="flex gap-2 pt-1">
        {situation.status === "open" && (
          <>
            <Button variant="quiet" onClick={() => store.deferSituation(situation.id)}>
              Defer
            </Button>
            <Button variant="ghost" onClick={() => store.resolveSituation(situation.id)}>
              Mark resolved
            </Button>
          </>
        )}
        {situation.status !== "open" && <Chip tone="success">{situation.status}</Chip>}
        {unit && (
          <Button variant="quiet" className="ml-auto" onClick={() => navigate(`/units/${unit.id}`)}>
            Open unit
          </Button>
        )}
      </div>
    </div>
  );
}

function CategoryActions({
  situation,
  draftUsed,
  setDraftUsed
}: {
  situation: Situation;
  draftUsed: boolean;
  setDraftUsed: (v: boolean) => void;
}) {
  const store = useAppStore();
  const navigate = useNavigate();

  if (situation.category === "financial") {
    const tenant = situation.unitId ? store.tenants.find((t) => t.id === store.units.find((u) => u.id === situation.unitId)?.tenantId) : null;
    return (
      <div className="flex flex-col gap-2 rounded-md border border-border bg-card p-3.5">
        <p className="font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground">Draft ready for review</p>
        <p className="text-[13.5px] leading-5 text-foreground">{draftMessage(situation)}</p>
        <div className="flex gap-2 pt-1">
          <Button
            onClick={() => {
              store.recordMessage(situation.unitId!, "call", `Called ${tenant?.contactName ?? "tenant"} re: ${situation.title}`, false);
            }}
          >
            Call {tenant?.contactName.split(" ")[0] ?? "tenant"}
          </Button>
          <Button
            variant="quiet"
            onClick={() => {
              store.recordMessage(situation.unitId!, "email", draftMessage(situation), true);
              setDraftUsed(true);
            }}
          >
            {draftUsed ? "Draft sent" : "Use draft"}
          </Button>
        </div>
        {situation.linkedWorkOrderId && <WorkOrderVerifyBlock workOrderId={situation.linkedWorkOrderId} />}
      </div>
    );
  }

  if (situation.category === "work-order" || situation.category === "inspection-followup") {
    return situation.linkedWorkOrderId ? <WorkOrderVerifyBlock workOrderId={situation.linkedWorkOrderId} /> : null;
  }

  if (situation.category === "tour" && situation.linkedTourId) {
    return (
      <Button onClick={() => navigate(`/tour/${situation.linkedTourId}`)} className="w-full">
        Open tour
      </Button>
    );
  }

  if (situation.category === "move-in" && situation.linkedMoveInPlanId) {
    return <MoveInChecklistBlock planId={situation.linkedMoveInPlanId} />;
  }

  if (situation.category === "move-out" && situation.linkedMoveOutPlanId) {
    return (
      <Button onClick={() => navigate(`/moveout/${situation.linkedMoveOutPlanId}`)} className="w-full">
        Open turnover
      </Button>
    );
  }

  if (situation.category === "compliance" && situation.linkedCoiId) {
    return <CoiBlock coiId={situation.linkedCoiId} />;
  }

  if (situation.category === "support" && situation.linkedSupportRequestId) {
    return <SupportRequestBlock requestId={situation.linkedSupportRequestId} />;
  }

  return null;
}

function WorkOrderVerifyBlock({ workOrderId }: { workOrderId: string }) {
  const wo = useAppStore((s) => s.workOrders.find((w) => w.id === workOrderId));
  const vendor = useAppStore((s) => s.vendors.find((v) => v.id === wo?.vendorId));
  const verifyWorkOrder = useAppStore((s) => s.verifyWorkOrder);
  const advanceWorkOrder = useAppStore((s) => s.advanceWorkOrder);
  if (!wo) return null;

  return (
    <div className="rounded-md border border-border bg-card p-3.5">
      <p className="font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground">
        Work order {wo.id.toUpperCase()} {vendor && `· ${vendor.name}`}
      </p>
      <p className="mt-1 text-[13.5px] text-foreground">{wo.description}</p>
      {wo.status === "reported" && (
        <Button className="mt-2.5" onClick={() => advanceWorkOrder(wo.id, "assigned", "Assigned to vendor.")}>
          Assign vendor
        </Button>
      )}
      {(wo.status === "assigned" || wo.status === "in-progress") && (
        <Button className="mt-2.5" onClick={() => advanceWorkOrder(wo.id, "ready-to-verify", "Vendor completed repair.")}>
          Mark ready to verify
        </Button>
      )}
      {wo.status === "ready-to-verify" && (
        <div className="mt-2.5 flex gap-2">
          <Button onClick={() => verifyWorkOrder(wo.id, "pass")}>Verify · Pass</Button>
          <Button variant="quiet" onClick={() => verifyWorkOrder(wo.id, "fail")}>
            Verify · Fail
          </Button>
        </div>
      )}
      {wo.status === "verified" && (
        <Button className="mt-2.5" onClick={() => advanceWorkOrder(wo.id, "closed", "Closed after verification.")}>
          Close work order
        </Button>
      )}
      {wo.status === "closed" && <p className="mt-2.5 font-mono text-[11px] uppercase text-success">Closed</p>}
    </div>
  );
}

function MoveInChecklistBlock({ planId }: { planId: string }) {
  const plan = useAppStore((s) => s.moveInPlans.find((p) => p.id === planId));
  const updateMoveInItem = useAppStore((s) => s.updateMoveInItem);
  if (!plan) return null;
  return (
    <div className="rounded-md border border-border bg-card p-3.5">
      <p className="mb-2 font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground">Readiness checklist · target {plan.targetMoveInDate}</p>
      <ul className="flex flex-col gap-2">
        {plan.items.map((item) => (
          <li key={item.key}>
            <label className="flex touch-target items-center gap-2.5 rounded-sm border border-border px-3 py-2">
              <input
                type="checkbox"
                checked={item.done}
                onChange={(e) => updateMoveInItem(plan.id, item.key, e.target.checked)}
                className="h-5 w-5 accent-primary"
                aria-label={item.label}
              />
              <span className={`text-[13.5px] ${item.done ? "text-muted-foreground line-through" : "text-foreground"}`}>{item.label}</span>
            </label>
          </li>
        ))}
      </ul>
      {plan.status === "ready" && <p className="mt-2.5 font-mono text-[11px] uppercase text-success">All readiness items complete — unit is ready</p>}
    </div>
  );
}

function CoiBlock({ coiId }: { coiId: string }) {
  const coi = useAppStore((s) => s.cois.find((c) => c.id === coiId));
  const advanceCoi = useAppStore((s) => s.advanceCoi);
  if (!coi) return null;
  const next = { flagged: "notified", notified: "received", received: "verified", verified: "verified" } as const;
  const label = { flagged: "Send notice", notified: "Mark received", received: "Mark verified", verified: "" } as const;
  return (
    <div className="rounded-md border border-border bg-card p-3.5">
      {coi.status === "verified" ? (
        <p className="font-mono text-[11px] uppercase text-success">Verified</p>
      ) : (
        <Button onClick={() => advanceCoi(coi.id, next[coi.status])}>{label[coi.status]}</Button>
      )}
    </div>
  );
}

function SupportRequestBlock({ requestId }: { requestId: string }) {
  const request = useAppStore((s) => s.supportRequests.find((r) => r.id === requestId));
  const advanceSupportRequest = useAppStore((s) => s.advanceSupportRequest);
  const recordMessage = useAppStore((s) => s.recordMessage);
  if (!request) return null;
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border bg-card p-3.5">
      <p className="text-[13.5px] leading-5 text-foreground">{request.body}</p>
      <div className="flex flex-wrap gap-2 pt-1">
        {request.status === "submitted" && <Button onClick={() => advanceSupportRequest(request.id, "acknowledged")}>Acknowledge</Button>}
        {request.status === "acknowledged" && (
          <Button
            onClick={() => {
              recordMessage(request.unitId, "text", "Thanks for flagging — dispatching an electrician today.", false);
              advanceSupportRequest(request.id, "in-progress");
            }}
          >
            Reply + start work
          </Button>
        )}
        {request.status === "in-progress" && <Button onClick={() => advanceSupportRequest(request.id, "resolved")}>Mark resolved</Button>}
        {request.status === "resolved" && <p className="font-mono text-[11px] uppercase text-success">Resolved</p>}
      </div>
    </div>
  );
}
